import { useState, useEffect } from "react";
import { useRegister } from "./RegisterContext";
import { toast } from "react-toastify";

/* ── Icons ──────────────────────────────────────────── */
const Icon = ({ d, size = 16, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconCash = () => <Icon size={22} d={["M12 1v22","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"]} />;
const IconDoorOpen = () => <Icon size={20} d={["M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2","M17 3v18h4V3z","M17 7h4"]} />;

/* ── Scoped CSS ─────────────────────────────────────── */
const OPEN_REGISTER_CSS = `
  .or-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex; align-items: center; justify-content: center; z-index: 200;
    backdrop-filter: blur(4px);
  }
  .or-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 400px;
    max-width: 95vw;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    overflow: hidden;
  }
  .or-header {
    display: flex; align-items: center; gap: 12px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .or-header-icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
  }
  .or-header-title {
    font-size: 16px; font-weight: 700; color: #fff;
  }
  .or-header-sub {
    font-size: 12px; color: rgba(255,255,255,0.4);
    margin-top: 2px;
  }

  .or-body {
    padding: 18px 24px;
    display: flex; flex-direction: column; gap: 14px;
  }

  .or-field label {
    display: block;
    font-size: 10px; font-weight: 600; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.3); text-transform: uppercase;
    margin-bottom: 4px;
  }
  .or-input {
    padding: 10px 14px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.05);
    color: #e2e4f0; font-size: 14px;
    outline: none; width: 100%;
    transition: border-color 0.2s;
  }
  .or-input:focus { border-color: #6366f1; }
  .or-input::placeholder { color: rgba(255,255,255,0.2); }
  
  .or-textarea { resize: vertical; min-height: 60px; }

  .or-actions {
    display: flex; gap: 10px;
    padding: 16px 24px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .or-btn-cancel {
    flex: 1;
    padding: 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.5); font-size: 13px; cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .or-btn-cancel:hover { border-color: rgba(255,255,255,0.2); color: #fff; }
  .or-btn-confirm {
    flex: 1; padding: 10px;
    border: none; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: opacity 0.15s, transform 0.1s;
  }
  .or-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
  .or-btn-confirm:hover:not(:disabled) { transform: scale(1.02); }
`;

export default function OpenRegisterModal() {
  // ✅ Destructure setOpenModalVisible from the context
  const { openModalVisible, setOpenModalVisible, openRegister } = useRegister();
  const [openingBalance, setOpeningBalance] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inject styles once
  useEffect(() => {
    const styleId = "open-register-modal-css";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent = OPEN_REGISTER_CSS;
      document.head.appendChild(el);
    }
  }, []);

  if (!openModalVisible) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(openingBalance);
    if (isNaN(amount) || amount < 0) {
      toast.warn("Enter a valid opening balance");
      return;
    }
    setSubmitting(true);
    try {
      await openRegister({ openingBalance: amount, note });
      toast.success("Register opened");
      setOpeningBalance(""); setNote("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not open register");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="or-modal-overlay" onClick={e => e.target === e.currentTarget && setOpenModalVisible(false)}>
      <div className="or-modal">
        <div className="or-header">
          <div className="or-header-icon">
            <IconDoorOpen />
          </div>
          <div>
            <div className="or-header-title">Open Register</div>
            <div className="or-header-sub">Start your shift with the opening cash amount</div>
          </div>
        </div>

        <div className="or-body">
          <div className="or-field">
            <label>Opening Balance</label>
            <div style={{ display: "flex" }}>
              <input
                type="number" step="0.001" min="0" autoFocus
                className="or-input"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none" }}
                value={openingBalance}
                onChange={e => setOpeningBalance(e.target.value)}
                placeholder="0.000"
              />
              <span style={{
                padding: "10px 14px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderLeft: "none",
                borderRadius: "0 10px 10px 0",
                color: "rgba(255,255,255,0.3)",
                fontSize: "11px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center"
              }}>
                TND
              </span>
            </div>
          </div>

          <div className="or-field">
            <label>Note (optional)</label>
            <textarea
              className="or-input or-textarea"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Morning shift"
            />
          </div>
        </div>

        <div className="or-actions">
          {/* ✅ Cancel uses the context setter directly */}
          <button type="button" className="or-btn-cancel" onClick={() => setOpenModalVisible(false)}>
            Cancel
          </button>
          <button type="submit" className="or-btn-confirm" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Opening…" : "Open Register & Start Shift"}
          </button>
        </div>
      </div>
    </div>
  );
}